import { z } from "zod";
import { McpApproveIn, McpAuthorizationOut, McpConnectionOut, McpRedirectOut } from "@/lib/api/schemas/mcp";
import { Auth } from "../auth";
import type { ActionReturnType } from "../types";
import { request, validateInput } from "../request";

/**
 * MCP connectors (e.g. Claude) acting as the user: the consent screen's authorization
 * request and the list of apps already connected. The consent calls handle their errors
 * locally, because the screen turns an expired or forbidden request into its own state.
 */
class McpEndpoint {
  public static readonly basePath = "mcp";
  public static readonly schemas = {
    authorization: {
      response: McpAuthorizationOut,
    },
    approve: {
      body: McpApproveIn,
      response: McpRedirectOut,
    },
    deny: {
      response: McpRedirectOut,
    },
    connections: {
      response: McpConnectionOut.array(),
    },
  };

  constructor(private api: Auth) {}

  public authorization(
    requestId: string,
  ): ActionReturnType<z.infer<typeof McpEndpoint.schemas.authorization.response>> {
    return request({
      log: ["Mcp", "authorization"],
      method: "get",
      path: [McpEndpoint.basePath, "authorizations", requestId],
      response: McpEndpoint.schemas.authorization.response,
      localErrorHandling: true,
    });
  }

  public async approve(
    requestId: string,
    body: z.input<typeof McpEndpoint.schemas.approve.body>,
  ): ActionReturnType<z.infer<typeof McpEndpoint.schemas.approve.response>> {
    const bodyValidated = validateInput(McpEndpoint.schemas.approve.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Mcp", "approve"],
      method: "post",
      path: [McpEndpoint.basePath, "authorizations", `${requestId}:approve`],
      json: bodyValidated.data,
      response: McpEndpoint.schemas.approve.response,
      localErrorHandling: true,
    });
  }

  public deny(requestId: string): ActionReturnType<z.infer<typeof McpEndpoint.schemas.deny.response>> {
    return request({
      log: ["Mcp", "deny"],
      method: "post",
      path: [McpEndpoint.basePath, "authorizations", `${requestId}:deny`],
      response: McpEndpoint.schemas.deny.response,
      localErrorHandling: true,
    });
  }

  public connections(): ActionReturnType<z.infer<typeof McpEndpoint.schemas.connections.response>> {
    return request({
      log: ["Mcp", "connections"],
      method: "get",
      path: [McpEndpoint.basePath, "connections"],
      response: McpEndpoint.schemas.connections.response,
    });
  }

  public disconnect(connectionId: string): ActionReturnType<undefined> {
    return request({
      log: ["Mcp", "disconnect"],
      method: "delete",
      path: [McpEndpoint.basePath, "connections", connectionId],
    });
  }
}

export { McpEndpoint };
